import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons
import { FiCheck } from 'react-icons/fi';
import { FiCalendar } from 'react-icons/fi';
import { FiMail } from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { FiArrowRight } from 'react-icons/fi';
import { FiStar } from 'react-icons/fi';
import { FiX } from 'react-icons/fi';
import { FiClock } from 'react-icons/fi';

// --- NEW: Animation Variants ---
// We define variants here to make our code cleaner and enable staggering

// Parent container variant: Manages the 'staggerChildren' effect
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // Each child will animate 0.2s after the previous one
    },
  },
};

// Child item variant: The animation each individual item will perform
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 100 }
  },
};

// A special variant for the "Most Important" step to make it pulse
const importantItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: [1, 1.03, 1], // NEW: Makes it pulse
    transition: {
      type: 'spring',
      stiffness: 100,
      scale: {
        // NEW: This part specifically controls the looping scale animation
        repeat: Infinity,
        duration: 1.5,
        ease: 'easeInOut',
      },
    },
  },
};
// --- End of New Variants ---


function App() {
  const [showPopup, setShowPopup] = useState(false);

  const getNextSunday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); 
    const daysUntilSunday = (0 - dayOfWeek + 7) % 7 || 7;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilSunday);
    
    return nextSunday.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleWhatsAppClick = () => {
    window.open('https://chat.whatsapp.com/FN6df5PeZO3FxInWVMlApC', '_blank');
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000); 
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 font-['Inter']">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <h1 className="text-2xl font-bold text-gray-800">10X AI AGENT WORKSHOP</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Congratulations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-8 mb-8">
            <div className="flex justify-center mb-4">
              {/* NEW: Added entry animation to the checkmark container */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.2 }}
                className="bg-green-500 rounded-full p-4"
              >
                {/* NEW: Made the checkmark itself pop in slightly after */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.4 }}
                >
                  <FiCheck className="text-4xl text-white" />
                </motion.div>
              </motion.div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-green-700 mb-4">
              Congratulations! 🎉
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              You're Successfully Registered!
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              You have successfully taken the first step to transform your business with sales automation and take it to the next level! 🚀
            </p>
          </div>
        </motion.div>

        {/* Payment Confirmation */}
        <motion.div
          // NEW: Using our itemVariants for a consistent springy load-in
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }} // This delay is now added to the stagger
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Payment Confirmed ✅</h3>
            </div>
            <div className="text-green-500 text-3xl">
              <FiCheck />
            </div>
          </div>
        </motion.div>

        {/* Next Steps Section */}
        <motion.div
          // NEW: Using the containerVariants to control the staggering of its children
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Next Steps 📋
          </h3>
          
          <div className="space-y-6">
            {/* Step 1 */}
            {/* NEW: Applying the itemVariants to this child */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
              <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiMail className="mr-2" />
                  Check Your Email 📧
                </h4>
                <p className="text-gray-600 mt-1">
                  Payment receipt and workshop confirmation email should be in your inbox. Please check it!
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            {/* NEW: Applying the itemVariants to this child */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-4 bg-purple-50 rounded-lg">
              <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiCalendar className="mr-2" />
                  Mark Your Calendar 📅
                </h4>
                <p className="text-gray-600 mt-1">
                  <strong> Sunday ({getNextSunday()}) from 10:00 AM to 1:00 PM IST</strong> - Don't forget this time! Set a reminder in your calendar.
                </p>
              </div>
            </motion.div>

            {/* Step 3 - Most Important */}
            {/* NEW: Applying the *special* importantItemVariants to make it pulse */}
            <motion.div variants={importantItemVariants} className="flex items-start space-x-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border-2 border-orange-200">
              <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiUsers className="mr-2" />
                  Join the Community (Most Important!) 🔥
                </h4>
                <p className="text-gray-600 mt-1">
                  <strong>This is the most important step!</strong> Join our community to get full value even before the workshop begins.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Main CTA - WhatsApp Community */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-xl p-8 text-white mb-8"
        >
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-4">
              🔥 The Final Important Step!
            </h3>
            
            <div className="bg-white/10 rounded-lg p-6 mb-6">
              <p className="text-lg leading-relaxed mb-4">
                Join our WhatsApp community and get these exclusive benefits:
              </p>
              
              {/* NEW: Staggering the benefits list as well */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid md:grid-cols-2 gap-4 text-left"
              >
                <motion.div variants={itemVariants} className="flex items-start space-x-2">
                  <FiCheck className="text-green-300 mt-1" />
                  <span>Direct workshop links & reminders</span>
                </motion.div>
                <motion.div variants={itemVariants} className="flex items-start space-x-2">
                  <FiCheck className="text-green-300 mt-1" />
                  <span>Exclusive pre-workshop sales tools info</span>
                </motion.div>
                <motion.div variants={itemVariants} className="flex items-start space-x-2">
                  <FiCheck className="text-green-300 mt-1" />
                  <span>Network with fellow entrepreneurs</span>
                </motion.div>
                <motion.div variants={itemVariants} className="flex items-start space-x-2">
                  <FiCheck className="text-green-300 mt-1" />
                  <span>Direct access to workshop host</span>
                </motion.div>
              </motion.div>
            </div>
            
            <motion.button
              // NEW: Added a repeating pulse animation to the button
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              // NEW: Made the hover/tap more "springy"
              whileHover={{ scale: 1.1, transition: { type: 'spring', stiffness: 300 } }}
              whileTap={{ scale: 0.95, transition: { type: 'spring', stiffness: 300 } }}
              onClick={handleWhatsAppClick}
              className="bg-white text-green-600 font-bold text-xl px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center mx-auto space-x-2"
            >
              <span>🚀 Yes, Add Me to the WhatsApp Community!</span>
              <FiArrowRight />
            </motion.button>
            
            <p className="text-green-100 mt-4 text-sm">
              Click above to join 500+ entrepreneurs! 👆
            </p>
          </div>
        </motion.div>

        {/* Value Reinforcement */}
        <motion.div
          variants={itemVariants} // NEW: Using variant
          initial="hidden"
          animate="visible"
          transition={{ delay: 1 }} // NEW: Simplified delay
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-white text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            {/* NEW: Added a repeating animation to the star */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            >
              <FiStar className="text-4xl text-yellow-300" />
            </motion.div>
          </div>
          <h3 className="text-2xl font-bold mb-4">Get Ready for Transformation! 🚀</h3>
          <p className="text-xl leading-relaxed">
            Say goodbye to manual work! Get ready to multiply your revenue by 10x with sales automation. 💪
          </p>
          <div className="flex items-center justify-center space-x-2 mt-4">
            <FiClock className="text-lg text-blue-100" />
            <p className="text-lg text-blue-100">
              See you live on {getNextSunday()} from 10:00 AM to 1:00 PM IST! 🔥
            </p>
          </div>
        </motion.div>

        {/* Secondary CTA */}
        <motion.div
          variants={itemVariants} // NEW: Using variant
          initial="hidden"
          animate="visible"
          transition={{ delay: 1.2 }} // NEW: Simplified delay
          className="text-center mb-8"
        >
          <p className="text-gray-600 mb-4">
            Haven't joined the WhatsApp community yet? Don't miss out!
          </p>
          <motion.button // NEW: Added motion to this button too
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleWhatsAppClick}
            className="bg-green-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-green-600 transition-colors duration-300 inline-flex items-center space-x-2"
          >
            <span>✅ Join WhatsApp Community Now</span>
            <FiArrowRight />
          </motion.button>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="border-t pt-8 mt-12 text-center text-gray-600"
        >
          <p className="mb-2">
            Questions? Contact us at:
            <a href="mailto:support@yinguru.com" className="text-blue-600 hover:underline ml-1">
              support@yinguru.com
            </a>
          </p>
          <p className="text-sm">
            ©2025 Sales Automation Workshop. All rights reserved.
          </p>
        </motion.div>
      </div>

      {/* WhatsApp Community Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              // NEW: Added a "spring" transition to the popup entry
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
            >
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
              >
                <FiX />
              </button>
              
              <div className="text-center">
                {/* NEW: Added a pulse to the icon in the popup */}
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                  className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4"
                >
                  <FiUsers className="text-3xl text-green-600" />
                </motion.div>
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Don't Miss Out! 🔥
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  500+ entrepreneurs are already in our exclusive WhatsApp community. Join now to get workshop links, sales tips, and network with like-minded business owners!
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    handleWhatsAppClick();
                    closePopup();
                  }}
                  className="bg-green-500 text-white font-bold text-lg px-6 py-3 rounded-full w-full hover:bg-green-600 transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <span>🚀 Join WhatsApp Community</span>
                  <FiArrowRight />
                </motion.button>
                
                <button
                  onClick={closePopup}
                  className="text-gray-500 hover:text-gray-700 mt-4 text-sm underline"
                >
                  I'll join later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;